import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GripVertical } from 'lucide-react';
import { useLanguage } from './LanguageContext';

export default function ImageComparison({ 
  beforeImage, 
  afterImage, 
  className = "",
  overlayText,
  watermarkText,
  logoUrl
}) {
  const { language } = useLanguage();
  const [isResizing, setIsResizing] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);

  const handleStart = () => setIsResizing(true);
  const handleEnd = () => setIsResizing(false);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = (x / rect.width) * 100;
    setSliderPosition(percent);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (!isResizing) return;
    handleMove(e.touches[0].clientX);
  };

  // Allow click to set position immediately
  const handleClick = (e) => {
    handleMove(e.clientX);
  };

  useEffect(() => {
    const handleGlobalEnd = () => setIsResizing(false);
    window.addEventListener('mouseup', handleGlobalEnd);
    window.addEventListener('touchend', handleGlobalEnd);
    return () => {
      window.removeEventListener('mouseup', handleGlobalEnd);
      window.removeEventListener('touchend', handleGlobalEnd);
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`relative w-full aspect-[3/4] rounded-2xl overflow-hidden select-none cursor-ew-resize group ring-2 ring-violet-500/50 ${className}`}
      onMouseDown={handleStart}
      onTouchStart={handleStart}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      onClick={handleClick}
    >
      {/* Before Image (Background) */}
      <img
        src={beforeImage}
        alt="Before"
        className="absolute inset-0 w-full h-full object-cover"
        draggable="false"
      />
      <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full bg-black/60 text-xs font-bold text-white/90 backdrop-blur-sm pointer-events-none">
        {language === 'he' ? 'לפני' : 'Before'}
      </div>

      {/* After Image (Clipped) */}
      <div 
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img
          src={afterImage}
          alt="After"
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
        
        {/* Overlay Elements (only visible on After side logic, but we want them to stick to the after image) */}
        {/* Actually, overlays should probably be on top of everything or just on the 'After' layer? 
            Let's put them on the 'After' layer so they get revealed. 
        */}
        {(overlayText || watermarkText || logoUrl) && (
            <div className="pointer-events-none absolute inset-0 flex flex-col justify-between z-20">
              {/* Top overlay text */}
              {overlayText && (
                <div className="flex justify-center mt-3 px-3">
                  <span className="inline-flex max-w-full items-center rounded-full bg-black/50 px-3 py-1 text-xs md:text-sm font-medium text-slate-100">
                    {overlayText}
                  </span>
                </div>
              )}

              {/* Bottom watermark + logo */}
              <div className="flex items-end justify-between px-3 pb-3 gap-3">
                {watermarkText && (
                  <span className="text-[10px] md:text-xs text-slate-100/80 bg-black/55 rounded-full px-2 py-1">
                    {watermarkText}
                  </span>
                )}
                {logoUrl && (
                  <div className="ml-auto">
                    <img
                      src={logoUrl}
                      alt="Logo watermark"
                      className="h-8 md:h-10 w-auto rounded bg-black/40 border border-white/10 object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full bg-gradient-to-r from-violet-600 to-pink-600 text-xs font-bold text-white shadow-lg pointer-events-none">
            {language === 'he' ? 'אחרי' : 'After'}
          </div>
      </div>

      {/* Slider Handle */}
      <div 
        className="absolute inset-y-0 w-1 bg-white cursor-ew-resize shadow-[0_0_10px_rgba(0,0,0,0.5)] z-30"
        style={{ left: `${sliderPosition}%` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-violet-600">
          <GripVertical className="w-5 h-5" />
        </div>
      </div>
      
      {/* Hint Text */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm pointer-events-none transition-opacity duration-300 ${isResizing ? 'opacity-0' : 'opacity-100'}`}>
        {language === 'he' ? 'הזז כדי להשוות' : 'Slide to compare'}
      </div>
    </div>
  );
}
